import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, FileSpreadsheet, Plus } from 'lucide-react';
import { api } from '../../services/api';
import EmptyState from '../EmptyState';
import { HubPanel, HubSurface, HubTable } from './HubPanel';
import { InlineError, QuoteStatusBadge, Spinner, apiErrorMessage, formatIls, useDateFormatter } from '../sales/shared';
import { getQuoteTypeLabel } from '../../types';
import type { Characterization, EngagementType } from '../../types';
import CharacterizationCard from './CharacterizationCard';
import CharacterizationFormModal from './CharacterizationFormModal';
import { toast } from '../../store/toastStore';

/**
 * "אפיונים והצעות" (P-07, D-74 §6 — was "לידים והצעות"): the customer's
 * "אפיון" cards first (D-50), then every quote with its lifecycle status.
 * "+ אפיון" (D-77) opens the characterization window; "הצעה חדשה" opens a new
 * quote with the customer preselected.
 */
export default function QuotesTab({
  orgId,
  orgName,
  canWrite,
  canAddCharacterization = canWrite,
  leadsConverted,
  onOpenEngagement,
  onOpenDocuments,
}: {
  orgId: string;
  orgName?: string;
  canWrite: boolean;
  /**
   * D-87 QX-24 — "+ אפיון" also on an ARCHIVED customer (GM / AM): the card
   * brings the customer back to active, like a signed characterization quote.
   */
  canAddCharacterization?: boolean;
  /** How many leads were converted into this customer (from the hub summary). */
  leadsConverted?: number;
  /** "פתיחת התקשרות מהאפיון ▾" — the page opens the full form of the type (D-74 §8). */
  onOpenEngagement: (type: EngagementType, card: Characterization) => void;
  onOpenDocuments: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const fmtDate = useDateFormatter();
  const [showNewCard, setShowNewCard] = useState(false);
  const queryClient = useQueryClient();

  const cardsQuery = useQuery({
    queryKey: ['characterizations', orgId],
    queryFn: () => api.getCharacterizations(orgId),
    enabled: !!orgId,
  });
  const cards = cardsQuery.data?.data ?? [];

  // "#char-<id>" (the overview line, the engagement banner) scrolls to the card.
  useEffect(() => {
    if (!location.hash.startsWith('#char-') || cards.length === 0) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [location.hash, cards.length]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['quotes', { organization_id: orgId }],
    queryFn: () => api.getQuotes({ organization_id: orgId, limit: 100 }),
    enabled: !!orgId,
  });
  const quotes = data?.data ?? [];

  return (
    <HubPanel
      title={t('organization.tabQuotes')}
      subtitle={leadsConverted ? t('organization.quotes.leadsConverted', { count: leadsConverted }) : t('organization.quotes.subtitle')}
      action={canWrite || canAddCharacterization ? (
        <span className="flex flex-wrap items-center gap-2">
          {canAddCharacterization && (
            <button type="button" className="btn btn-secondary" onClick={() => setShowNewCard(true)}>
              <Plus size={16} aria-hidden="true" /> {t('characterization.add')}
            </button>
          )}
          {canWrite && (
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate(`/quotes?create=1&organization_id=${orgId}`)}>
              <Plus size={16} aria-hidden="true" /> {t('organization.actions.newQuote')}
            </button>
          )}
        </span>
      ) : undefined}
    >
      {/* P-07 (D-50) — the "אפיון" cards, before the quotes. */}
      {cardsQuery.isError ? (
        <InlineError>{apiErrorMessage(cardsQuery.error, t('characterization.loadError'))}</InlineError>
      ) : cards.length > 0 && (
        <section className="mb-6 space-y-3" aria-label={t('characterization.sectionTitle')}>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-surface-900">
            <ClipboardList size={16} aria-hidden="true" /> {t('characterization.sectionTitle')}
          </h3>
          {cards.map((card) => (
            <CharacterizationCard
              key={card.id}
              card={card}
              orgId={orgId}
              canWrite={canWrite}
              onOpenEngagement={onOpenEngagement}
              onOpenDocuments={onOpenDocuments}
            />
          ))}
        </section>
      )}
      {cards.length > 0 && <h3 className="mb-3 text-sm font-semibold text-surface-900">{t('characterization.quotesTitle')}</h3>}

      {showNewCard && (
        <CharacterizationFormModal
          organizationId={orgId}
          organizationName={orgName}
          cards={cards}
          onClose={() => setShowNewCard(false)}
          onSaved={() => {
            setShowNewCard(false);
            toast('success', t('characterization.form.created'));
            cardsQuery.refetch();
            queryClient.invalidateQueries({ queryKey: ['customer-hub-summary', orgId] });
            queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
            queryClient.invalidateQueries({ queryKey: ['organizations'] });
          }}
        />
      )}

      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <InlineError>{apiErrorMessage(error, t('quotes.actionError'))}</InlineError>
      ) : quotes.length === 0 ? (
        <HubSurface>
          <EmptyState icon={FileSpreadsheet} title={t('organization.quotes.empty')} />
        </HubSurface>
      ) : (
        <HubSurface>
          <HubTable>
            <table className="table-modern">
              <thead>
                <tr>
                  <th>{t('quotes.quotetitle')}</th>
                  <th className="hidden md:table-cell">{t('quotes.type')}</th>
                  <th>{t('quotes.status')}</th>
                  <th className="text-end">{t('quotes.grandTotal')}</th>
                  <th className="hidden sm:table-cell text-end">{t('quotes.createdAt')}</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id}>
                    <td className="md:max-w-xs">
                      <Link to={`/quotes/${q.id}`} className="block max-w-full break-words md:truncate font-medium text-surface-900 hover:text-primary-700 hover:underline underline-offset-2">
                        {q.title}
                      </Link>
                      <span className="text-xs text-surface-600 tabular-nums" dir="ltr">
                        #{q.quote_number}
                        {(q.revision_number ?? 1) > 1 && <> · {t('quotes.revision.short', { number: q.revision_number })}</>}
                      </span>
                    </td>
                    <td className="hidden md:table-cell text-surface-600">{getQuoteTypeLabel(q.quote_type, t)}</td>
                    <td><QuoteStatusBadge status={q.status} /></td>
                    <td className="text-end tabular-nums whitespace-nowrap">{formatIls(q.grand_total)}</td>
                    <td className="hidden sm:table-cell text-end tabular-nums whitespace-nowrap text-surface-600">{fmtDate(q.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HubTable>
        </HubSurface>
      )}
    </HubPanel>
  );
}
